// src/features/workflow/logic.ts
// Business logic for workflow pipelines — framework-agnostic, zero React imports.

// --- Status & Type Mappings ---

export type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'secondary' | 'primary' | 'default';

export interface StatusMapping {
  variant: BadgeVariant;
  labelKey: string;
}

export const pipelineStatusMap: Record<string, StatusMapping> = {
  PIPELINE_STATUS_DRAFT:    { variant: 'secondary', labelKey: 'workflow.status.draft' },
  PIPELINE_STATUS_ACTIVE:   { variant: 'success',   labelKey: 'workflow.status.active' },
  PIPELINE_STATUS_PAUSED:   { variant: 'warning',   labelKey: 'workflow.status.paused' },
  PIPELINE_STATUS_ARCHIVED: { variant: 'default',   labelKey: 'workflow.status.archived' },
};

export const executionStatusMap: Record<string, StatusMapping> = {
  EXECUTION_STATUS_PENDING:           { variant: 'secondary', labelKey: 'workflow.exec.pending' },
  EXECUTION_STATUS_RUNNING:           { variant: 'info',      labelKey: 'workflow.exec.running' },
  EXECUTION_STATUS_COMPLETED:         { variant: 'success',   labelKey: 'workflow.exec.completed' },
  EXECUTION_STATUS_FAILED:            { variant: 'error',     labelKey: 'workflow.exec.failed' },
  EXECUTION_STATUS_CANCELLED:         { variant: 'secondary', labelKey: 'workflow.exec.cancelled' },
  EXECUTION_STATUS_TIMED_OUT:         { variant: 'error',     labelKey: 'workflow.exec.timedOut' },
  EXECUTION_STATUS_PAUSED:            { variant: 'warning',   labelKey: 'workflow.exec.paused' },
  EXECUTION_STATUS_WAITING_FOR_INPUT: { variant: 'warning',   labelKey: 'workflow.exec.waiting' },
};

export const triggerTypeMap: Record<string, { labelKey: string; icon: string }> = {
  TRIGGER_TYPE_MANUAL:          { labelKey: 'workflow.trigger.manual',     icon: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z' },
  TRIGGER_TYPE_SCHEDULE:        { labelKey: 'workflow.trigger.schedule',   icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
  TRIGGER_TYPE_WEBHOOK:         { labelKey: 'workflow.trigger.webhook',    icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1' },
  TRIGGER_TYPE_EVENT:           { labelKey: 'workflow.trigger.event',      icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
  TRIGGER_TYPE_FORM_SUBMISSION: { labelKey: 'workflow.trigger.form',       icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  TRIGGER_TYPE_ENTITY_CHANGE:   { labelKey: 'workflow.trigger.entity',     icon: 'M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7C5 4 4 5 4 7zm4-3v16' },
};

export const nodeTypeCategoryMap: Record<string, { labelKey: string; color: string }> = {
  NODE_TYPE_CATEGORY_TRIGGER:      { labelKey: 'workflow.nodeCategory.trigger',      color: '#8b5cf6' },
  NODE_TYPE_CATEGORY_ACTION:       { labelKey: 'workflow.nodeCategory.action',       color: '#3b82f6' },
  NODE_TYPE_CATEGORY_CONDITION:    { labelKey: 'workflow.nodeCategory.condition',    color: '#f59e0b' },
  NODE_TYPE_CATEGORY_TRANSFORMER:  { labelKey: 'workflow.nodeCategory.transformer',  color: '#10b981' },
  NODE_TYPE_CATEGORY_INTEGRATION:  { labelKey: 'workflow.nodeCategory.integration',  color: '#6366f1' },
  NODE_TYPE_CATEGORY_UTILITY:      { labelKey: 'workflow.nodeCategory.utility',      color: '#6b7280' },
  NODE_TYPE_CATEGORY_AI:           { labelKey: 'workflow.nodeCategory.ai',           color: '#06b6d4' },
  NODE_TYPE_CATEGORY_CUSTOM:       { labelKey: 'workflow.nodeCategory.custom',       color: '#ec4899' },
};

export const credentialTypeMap: Record<string, { labelKey: string; icon: string }> = {
  CREDENTIAL_TYPE_API_KEY:       { labelKey: 'workflow.cred.apiKey',       icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z' },
  CREDENTIAL_TYPE_OAUTH2:        { labelKey: 'workflow.cred.oauth2',       icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' },
  CREDENTIAL_TYPE_BASIC_AUTH:    { labelKey: 'workflow.cred.basicAuth',    icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
  CREDENTIAL_TYPE_BEARER_TOKEN:  { labelKey: 'workflow.cred.bearerToken',  icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
  CREDENTIAL_TYPE_CUSTOM_HEADER: { labelKey: 'workflow.cred.customHeader', icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4' },
  CREDENTIAL_TYPE_DATABASE:      { labelKey: 'workflow.cred.database',     icon: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4' },
  CREDENTIAL_TYPE_SSH_KEY:       { labelKey: 'workflow.cred.sshKey',       icon: 'M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z' },
  CREDENTIAL_TYPE_WEBHOOK_SECRET:{ labelKey: 'workflow.cred.webhookSecret',icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101' },
};

export const triggerTypeOptions = [
  { value: 'TRIGGER_TYPE_MANUAL', labelKey: 'workflow.trigger.manual' },
  { value: 'TRIGGER_TYPE_SCHEDULE', labelKey: 'workflow.trigger.schedule' },
  { value: 'TRIGGER_TYPE_WEBHOOK', labelKey: 'workflow.trigger.webhook' },
  { value: 'TRIGGER_TYPE_EVENT', labelKey: 'workflow.trigger.event' },
  { value: 'TRIGGER_TYPE_FORM_SUBMISSION', labelKey: 'workflow.trigger.form' },
  { value: 'TRIGGER_TYPE_ENTITY_CHANGE', labelKey: 'workflow.trigger.entity' },
];

export const errorHandlingOptions = [
  { value: 'ERROR_HANDLING_STOP_ON_ERROR', labelKey: 'workflow.errorHandling.stop' },
  { value: 'ERROR_HANDLING_CONTINUE_ON_ERROR', labelKey: 'workflow.errorHandling.continue' },
  { value: 'ERROR_HANDLING_RETRY_ON_ERROR', labelKey: 'workflow.errorHandling.retry' },
];

export const credentialTypeOptions = [
  { value: 'CREDENTIAL_TYPE_API_KEY', labelKey: 'workflow.cred.apiKey' },
  { value: 'CREDENTIAL_TYPE_OAUTH2', labelKey: 'workflow.cred.oauth2' },
  { value: 'CREDENTIAL_TYPE_BASIC_AUTH', labelKey: 'workflow.cred.basicAuth' },
  { value: 'CREDENTIAL_TYPE_BEARER_TOKEN', labelKey: 'workflow.cred.bearerToken' },
  { value: 'CREDENTIAL_TYPE_CUSTOM_HEADER', labelKey: 'workflow.cred.customHeader' },
  { value: 'CREDENTIAL_TYPE_DATABASE', labelKey: 'workflow.cred.database' },
  { value: 'CREDENTIAL_TYPE_SSH_KEY', labelKey: 'workflow.cred.sshKey' },
  { value: 'CREDENTIAL_TYPE_WEBHOOK_SECRET', labelKey: 'workflow.cred.webhookSecret' },
];

export const environmentTypeOptions = [
  { value: 'ENVIRONMENT_TYPE_DEVELOPMENT', labelKey: 'workflow.env.development' },
  { value: 'ENVIRONMENT_TYPE_STAGING', labelKey: 'workflow.env.staging' },
  { value: 'ENVIRONMENT_TYPE_PRODUCTION', labelKey: 'workflow.env.production' },
];

// --- Data Accessors ---

export function getEntityId(item: any): string {
  return item?.entity?.id || item?.id || '';
}

export function getPipelineName(item: any): string {
  return item?.name || 'Untitled Pipeline';
}

export function getPipelineStatus(item: any): string {
  return item?.status || 'PIPELINE_STATUS_DRAFT';
}

export function getTriggerType(item: any): string {
  return item?.trigger_type || 'TRIGGER_TYPE_MANUAL';
}

export function getExecutionStatus(item: any): string {
  return item?.status || 'EXECUTION_STATUS_PENDING';
}

// --- Formatters ---

export function formatDate(dateStr: string | undefined | null, locale = 'en'): string {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

export function formatDateTime(dateStr: string | undefined | null, locale = 'en'): string {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleString(locale, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

export function formatRelativeTime(dateStr: string | undefined | null): string {
  if (!dateStr) return '';
  try {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diffSec = Math.floor((now - then) / 1000);
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
    return formatDate(dateStr);
  } catch {
    return '';
  }
}

export function formatDuration(ms: number | undefined | null): string {
  if (!ms && ms !== 0) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  return `${mins}m ${secs}s`;
}

export function formatCron(expr: string): string {
  if (!expr) return '-';
  return expr;
}

// --- Filtering & Sorting ---

export function filterPipelines(
  items: any[],
  search: string,
  statusFilter: string,
  triggerFilter: string,
): any[] {
  let filtered = items;

  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(
      (p) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q),
    );
  }

  if (statusFilter && statusFilter !== 'all') {
    filtered = filtered.filter((p) => p.status === statusFilter);
  }

  if (triggerFilter && triggerFilter !== 'all') {
    filtered = filtered.filter((p) => p.trigger_type === triggerFilter);
  }

  return filtered;
}

export function sortPipelinesByRecent(items: any[]): any[] {
  return [...items].sort((a, b) => {
    const da = new Date(a.updated_at || a.created_at || 0).getTime();
    const db = new Date(b.updated_at || b.created_at || 0).getTime();
    return db - da;
  });
}

// --- Validation ---

export function validatePipelineForm(data: { name: string }): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.name.trim()) {
    errors.name = 'validation.required';
  } else if (data.name.trim().length < 2) {
    errors.name = 'validation.minLength';
  } else if (data.name.trim().length > 200) {
    errors.name = 'validation.maxLength';
  }
  return errors;
}

export function validateCredentialForm(data: { name: string; credential_type: string }): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.name.trim()) errors.name = 'validation.required';
  if (!data.credential_type || data.credential_type === 'CREDENTIAL_TYPE_UNSPECIFIED') {
    errors.credential_type = 'validation.required';
  }
  return errors;
}

// --- Builders ---

export function buildCreatePipelinePayload(data: {
  name: string;
  description: string;
  trigger_type: string;
  error_handling: string;
  category: string;
}): any {
  return {
    name: data.name.trim(),
    description: data.description.trim() || undefined,
    trigger_type: data.trigger_type,
    error_handling: data.error_handling,
    category: data.category.trim() || undefined,
  };
}

// --- Creation Mode ---

export type CreateMode = 'blank' | 'template' | 'import';

export interface CreateModeOption {
  mode: CreateMode;
  labelKey: string;
  descKey: string;
  iconPath: string;
}

export const createModeOptions: CreateModeOption[] = [
  {
    mode: 'blank',
    labelKey: 'workflow.createMode.blank',
    descKey: 'workflow.createMode.blankDesc',
    iconPath: 'M12 4v16m8-8H4',
  },
  {
    mode: 'template',
    labelKey: 'workflow.createMode.template',
    descKey: 'workflow.createMode.templateDesc',
    iconPath: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm0 8a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zm10 0a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1v-6z',
  },
  {
    mode: 'import',
    labelKey: 'workflow.createMode.import',
    descKey: 'workflow.createMode.importDesc',
    iconPath: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12',
  },
];

// Trigger type visual cards with descriptions
export interface TriggerTypeCard {
  value: string;
  labelKey: string;
  descKey: string;
  iconPath: string;
}

export const triggerTypeCards: TriggerTypeCard[] = [
  {
    value: 'TRIGGER_TYPE_MANUAL',
    labelKey: 'workflow.trigger.manual',
    descKey: 'workflow.trigger.manualDesc',
    iconPath: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z',
  },
  {
    value: 'TRIGGER_TYPE_SCHEDULE',
    labelKey: 'workflow.trigger.schedule',
    descKey: 'workflow.trigger.scheduleDesc',
    iconPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  {
    value: 'TRIGGER_TYPE_WEBHOOK',
    labelKey: 'workflow.trigger.webhook',
    descKey: 'workflow.trigger.webhookDesc',
    iconPath: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
  },
  {
    value: 'TRIGGER_TYPE_EVENT',
    labelKey: 'workflow.trigger.event',
    descKey: 'workflow.trigger.eventDesc',
    iconPath: 'M13 10V3L4 14h7v7l9-11h-7z',
  },
  {
    value: 'TRIGGER_TYPE_FORM_SUBMISSION',
    labelKey: 'workflow.trigger.form',
    descKey: 'workflow.trigger.formDesc',
    iconPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  },
  {
    value: 'TRIGGER_TYPE_ENTITY_CHANGE',
    labelKey: 'workflow.trigger.entity',
    descKey: 'workflow.trigger.entityDesc',
    iconPath: 'M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7C5 4 4 5 4 7zm4-3v16',
  },
];

// Error handling cards with descriptions
export interface ErrorHandlingCard {
  value: string;
  labelKey: string;
  descKey: string;
  iconPath: string;
}

export const errorHandlingCards: ErrorHandlingCard[] = [
  {
    value: 'ERROR_HANDLING_STOP_ON_ERROR',
    labelKey: 'workflow.errorHandling.stop',
    descKey: 'workflow.errorHandling.stopDesc',
    iconPath: 'M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  {
    value: 'ERROR_HANDLING_CONTINUE_ON_ERROR',
    labelKey: 'workflow.errorHandling.continue',
    descKey: 'workflow.errorHandling.continueDesc',
    iconPath: 'M13 5l7 7-7 7M5 5l7 7-7 7',
  },
  {
    value: 'ERROR_HANDLING_RETRY_ON_ERROR',
    labelKey: 'workflow.errorHandling.retry',
    descKey: 'workflow.errorHandling.retryDesc',
    iconPath: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  },
];

export function validateYamlImport(yaml: string): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!yaml.trim()) {
    errors.yaml = 'validation.required';
  } else if (yaml.trim().length < 10) {
    errors.yaml = 'workflow.validation.yamlTooShort';
  }
  return errors;
}

export function filterTemplates(
  templates: any[],
  search: string,
  category: string,
): any[] {
  let items = templates;
  if (search) {
    const q = search.toLowerCase();
    items = items.filter(
      (tmpl: any) =>
        (tmpl.name || '').toLowerCase().includes(q) ||
        (tmpl.description || '').toLowerCase().includes(q) ||
        (tmpl.tagline || '').toLowerCase().includes(q) ||
        (tmpl.tags || []).some((tag: string) => tag.toLowerCase().includes(q)),
    );
  }
  if (category && category !== 'all') {
    items = items.filter((tmpl: any) => tmpl.category === category);
  }
  return items;
}

export function getTemplateCategories(templates: any[]): string[] {
  const cats = new Set<string>();
  for (const t of templates) {
    if (t.category) cats.add(t.category);
  }
  return Array.from(cats).sort();
}

// --- UI Helpers ---

export function getStatusColor(status: string): string {
  const map: Record<string, string> = {
    EXECUTION_STATUS_COMPLETED: '#10b981',
    EXECUTION_STATUS_RUNNING: '#3b82f6',
    EXECUTION_STATUS_FAILED: '#ef4444',
    EXECUTION_STATUS_PENDING: '#6b7280',
    EXECUTION_STATUS_CANCELLED: '#6b7280',
    EXECUTION_STATUS_TIMED_OUT: '#ef4444',
    EXECUTION_STATUS_PAUSED: '#f59e0b',
    EXECUTION_STATUS_WAITING_FOR_INPUT: '#f59e0b',
    PIPELINE_STATUS_ACTIVE: '#10b981',
    PIPELINE_STATUS_DRAFT: '#6b7280',
    PIPELINE_STATUS_PAUSED: '#f59e0b',
    PIPELINE_STATUS_ARCHIVED: '#374151',
  };
  return map[status] || '#6b7280';
}

export function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function getAvatarColor(name: string): string {
  const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1', '#ef4444'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

// Pipeline tab definitions
export const PIPELINE_TABS = [
  { id: 'overview',     labelKey: 'workflow.tab.overview' },
  { id: 'executions',   labelKey: 'workflow.tab.executions' },
  { id: 'versions',     labelKey: 'workflow.tab.versions' },
  { id: 'schedules',    labelKey: 'workflow.tab.schedules' },
  { id: 'environments', labelKey: 'workflow.tab.environments' },
  { id: 'tests',        labelKey: 'workflow.tab.tests' },
  { id: 'settings',     labelKey: 'workflow.tab.settings' },
] as const;

export type PipelineTab = typeof PIPELINE_TABS[number]['id'];

// --- Badge variant coercion ---
// The Badge component has no 'info' variant; map it to 'primary' so status
// maps that carry 'info' stay type-safe at call sites.
export function toBadgeVariant(
  v: BadgeVariant,
): 'default' | 'error' | 'primary' | 'secondary' | 'success' | 'warning' {
  return v === 'info' ? 'primary' : v;
}

// --- Schedules ---

export const scheduleTypeOptions = [
  { value: 'SCHEDULE_TYPE_CRON', labelKey: 'workflow.schedule.cron' },
  { value: 'SCHEDULE_TYPE_INTERVAL', labelKey: 'workflow.schedule.interval' },
  { value: 'SCHEDULE_TYPE_ONCE', labelKey: 'workflow.schedule.once' },
];

// Small, dependency-free cron humanizer for the common shapes.
// Falls back to the raw expression for anything it does not recognise.
export function describeCron(expr: string): string | null {
  if (!expr) return null;
  const parts = expr.trim().split(/\s+/);
  if (parts.length < 5) return null;
  const [min, hour, dom, mon, dow] = parts;
  if (min === '*' && hour === '*' && dom === '*' && mon === '*' && dow === '*') return 'Every minute';
  if (dom === '*' && mon === '*' && dow === '*' && /^\d+$/.test(min) && /^\d+$/.test(hour)) {
    return `Daily at ${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
  }
  if (hour === '*' && dom === '*' && mon === '*' && dow === '*' && /^\d+$/.test(min)) {
    return `Hourly at :${min.padStart(2, '0')}`;
  }
  return null;
}

// Preview the next run for an interval schedule, relative to now.
export function previewIntervalNextRun(intervalSeconds: number): string {
  if (!intervalSeconds || intervalSeconds <= 0) return '-';
  const next = new Date(Date.now() + intervalSeconds * 1000);
  return next.toISOString();
}

export function formatInterval(seconds: number): string {
  if (!seconds || seconds <= 0) return '-';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

export function validateScheduleForm(data: {
  name: string;
  schedule_type: string;
  cron_expression: string;
  interval_seconds: number;
}): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.name.trim()) errors.name = 'validation.required';
  if (data.schedule_type === 'SCHEDULE_TYPE_CRON' && !data.cron_expression.trim()) {
    errors.cron_expression = 'validation.required';
  }
  if (data.schedule_type === 'SCHEDULE_TYPE_INTERVAL' && (!data.interval_seconds || data.interval_seconds <= 0)) {
    errors.interval_seconds = 'validation.required';
  }
  return errors;
}

// --- Environments: secret masking ---

const SECRET_KEY_RE = /(secret|token|password|passwd|api[_-]?key|private[_-]?key|credential)/i;

export function isSecretKey(key: string): boolean {
  return SECRET_KEY_RE.test(key);
}

export function maskSecret(value: string): string {
  if (!value) return '';
  if (value.length <= 4) return '••••';
  return `${'•'.repeat(Math.min(value.length - 2, 12))}${value.slice(-2)}`;
}

// --- Decisions (approvals) ---

export const decisionVerdictMap: Record<string, StatusMapping> = {
  DECISION_VERDICT_APPROVED:  { variant: 'success',   labelKey: 'workflow.decision.approved' },
  DECISION_VERDICT_REJECTED:  { variant: 'error',     labelKey: 'workflow.decision.rejected' },
  DECISION_VERDICT_ABSTAINED: { variant: 'secondary', labelKey: 'workflow.decision.abstained' },
};

export const decisionStatusMap: Record<string, StatusMapping> = {
  DECISION_STATUS_PENDING:   { variant: 'warning',   labelKey: 'workflow.decision.pending' },
  DECISION_STATUS_DECIDED:   { variant: 'success',   labelKey: 'workflow.decision.decided' },
  DECISION_STATUS_DELEGATED: { variant: 'info',      labelKey: 'workflow.decision.delegated' },
  DECISION_STATUS_EXPIRED:   { variant: 'error',     labelKey: 'workflow.decision.expired' },
};

// --- Executions: status filter options ---

export const executionStatusFilterOptions = [
  { value: 'all', labelKey: 'workflow.filter.allStatuses' },
  { value: 'EXECUTION_STATUS_RUNNING', labelKey: 'workflow.exec.running' },
  { value: 'EXECUTION_STATUS_COMPLETED', labelKey: 'workflow.exec.completed' },
  { value: 'EXECUTION_STATUS_FAILED', labelKey: 'workflow.exec.failed' },
  { value: 'EXECUTION_STATUS_CANCELLED', labelKey: 'workflow.exec.cancelled' },
  { value: 'EXECUTION_STATUS_PAUSED', labelKey: 'workflow.exec.paused' },
  { value: 'EXECUTION_STATUS_WAITING_FOR_INPUT', labelKey: 'workflow.exec.waiting' },
];

// A running/paused/waiting execution can be cancelled; a terminal one can be retried.
export function isExecutionActive(status: string): boolean {
  return (
    status === 'EXECUTION_STATUS_RUNNING' ||
    status === 'EXECUTION_STATUS_PENDING' ||
    status === 'EXECUTION_STATUS_PAUSED' ||
    status === 'EXECUTION_STATUS_WAITING_FOR_INPUT'
  );
}

export function isExecutionRetryable(status: string): boolean {
  return (
    status === 'EXECUTION_STATUS_FAILED' ||
    status === 'EXECUTION_STATUS_CANCELLED' ||
    status === 'EXECUTION_STATUS_TIMED_OUT'
  );
}

// Build an Entity ref carrying only the id — the id-path endpoints ignore the
// rest, but the generated request types require an Entity shape.
export function entityRef(id: string): any {
  return { id };
}
